/**
 * TrustBox widget bootstrap — certified rating badges only (no invitation script).
 */

const LOADED_ATTR = 'data-trustpilot-loaded'
const NEAR_VIEWPORT_ATTR = 'data-trustpilot-near-viewport'
const WIDGET_SELECTOR = '[data-trustpilot-widget]'
const ANNOUNCEMENT_SELECTOR = '[data-trustpilot-announcement]'
export const TRUSTPILOT_LAZY_ROOT_MARGIN = '600px 0px'
const MAX_BOOTSTRAP_RETRIES = 50
const RETRY_DELAY_MS = 100
/** Dispatched by snippets/trustpilot-loader.liquid once the bootstrap loads. */
export const TRUSTPILOT_BOOTSTRAP_READY_EVENT = 'trustpilot:bootstrap-ready'

/**
 * @param {string | undefined | null} settingsLocale
 * @param {string | undefined | null} requestLocale
 * @returns {string}
 */
export function resolveTrustpilotLocale(settingsLocale, requestLocale) {
  const trimmed = settingsLocale?.trim()
  if (trimmed) return trimmed

  const iso = requestLocale?.trim() || 'fr-FR'
  if (iso === 'fr') return 'fr-FR'
  if (iso === 'en') return 'en-US'
  return iso
}

/**
 * @param {'micro-combo' | 'micro-star' | string} variant
 * @param {{ microCombo?: string, microStar?: string }} templates
 * @returns {string}
 */
export function resolveTrustpilotTemplateId(variant, templates = {}) {
  if (variant === 'micro-star') {
    return templates.microStar || '5419b732fbfb950b10de65e5'
  }
  return templates.microCombo || '5419b6ffb0d04a076446a9af'
}

/**
 * @param {{ enabled?: boolean, showSurface?: boolean }} options
 * @returns {boolean}
 */
export function shouldRenderTrustpilotSurface({ enabled, showSurface }) {
  return Boolean(enabled && showSurface)
}

/**
 * @param {number} stars
 * @param {string} locale
 * @returns {string}
 */
export function formatTrustpilotScoreDisplay(stars, locale) {
  const value = Number(stars)
  if (!Number.isFinite(value)) return ''

  const formatted = value.toFixed(1)
  if (locale.trim().toLowerCase().startsWith('fr')) {
    return formatted.replace('.', ',')
  }
  return formatted
}

/**
 * @param {number} count
 * @param {string} locale
 * @returns {string}
 */
export function formatTrustpilotReviewCount(count, locale) {
  const value = Number(count)
  if (!Number.isFinite(value)) return ''

  const localeTag = locale.trim().toLowerCase().startsWith('fr')
    ? 'fr-FR'
    : 'en-US'
  return new Intl.NumberFormat(localeTag, { useGrouping: true })
    .format(value)
    .replace(/\u202f/g, ' ')
    .replace(/,/g, ' ')
}

/**
 * @param {string | undefined | null} text
 * @returns {{ stars: number, reviewCount: number } | null}
 */
export function parseTrustpilotAccessibleName(text) {
  const normalized = text?.replace(/\u00a0/g, ' ').trim()
  if (!normalized) return null

  const scoreMatch = normalized.match(/(\d+[,.]\d+)\s*(?:sur|out of)\s*5/i)
  const reviewsMatch = normalized.match(/([\d\s.,]+)\s*(?:avis|reviews)\b/i)

  if (!scoreMatch || !reviewsMatch) return null

  const stars = Number.parseFloat(scoreMatch[1].replace(',', '.'))
  const reviewCount = Number.parseInt(reviewsMatch[1].replace(/[^\d]/g, ''), 10)

  if (!Number.isFinite(stars) || !Number.isFinite(reviewCount)) return null

  return { stars, reviewCount }
}

/**
 * @param {HTMLElement} container
 */
export function applyAnnouncementFallbackFromDataset(container) {
  if (container.dataset.trustpilotAnnouncementHydrated === 'true') return

  const scoreEl = container.querySelector(
    '[data-trustpilot-announcement-score]'
  )
  const reviewsEl = container.querySelector(
    '[data-trustpilot-announcement-reviews]'
  )
  if (
    scoreEl instanceof HTMLElement &&
    scoreEl.textContent?.trim() &&
    reviewsEl instanceof HTMLElement &&
    reviewsEl.textContent?.trim()
  ) {
    return
  }

  const stars = Number(container.dataset.fallbackStars)
  const reviewCount = Number(container.dataset.fallbackReviews)
  if (!Number.isFinite(stars) || !Number.isFinite(reviewCount)) return

  applyAnnouncementTrustpilotStats(container, { stars, reviewCount })
  container.dataset.trustpilotAnnouncementHydrated = 'fallback'
}

/**
 * @param {HTMLElement} container
 */
function parseAnnouncementTrustpilotFromWidget(container) {
  const widget = container.querySelector('[data-trustpilot-widget]')
  const iframe = widget?.querySelector('iframe')
  if (!(iframe instanceof HTMLIFrameElement)) return false

  const accessibleName =
    iframe.getAttribute('title') ||
    iframe.getAttribute('aria-label') ||
    widget?.getAttribute('aria-label') ||
    ''

  const stats = parseTrustpilotAccessibleName(accessibleName)
  if (!stats) return false

  applyAnnouncementTrustpilotStats(container, stats)
  return true
}

/**
 * @param {HTMLElement} container
 */
export function observeAnnouncementTrustpilotWidget(container) {
  if (parseAnnouncementTrustpilotFromWidget(container)) return

  const widget = container.querySelector('[data-trustpilot-widget]')
  if (!widget) return

  if (container.dataset.trustpilotAnnouncementObserver === 'true') return
  container.dataset.trustpilotAnnouncementObserver = 'true'

  const observer = new MutationObserver(() => {
    if (parseAnnouncementTrustpilotFromWidget(container)) {
      observer.disconnect()
      container.dataset.trustpilotAnnouncementObserver = 'false'
    }
  })

  observer.observe(widget, {
    childList: true,
    subtree: true,
    attributes: true,
    attributeFilter: ['title', 'aria-label']
  })
}

/**
 * @param {HTMLElement} container
 * @param {{ stars: number, reviewCount: number }} stats
 */
export function applyAnnouncementTrustpilotStats(container, stats) {
  const locale = container.dataset.locale || 'fr-FR'
  const scoreSuffix = container.dataset.scoreSuffix || ''
  const reviewsSuffix = container.dataset.reviewsSuffix || ''
  const scoreEl = container.querySelector(
    '[data-trustpilot-announcement-score]'
  )
  const reviewsEl = container.querySelector(
    '[data-trustpilot-announcement-reviews]'
  )
  const liveEl = container.querySelector('[data-trustpilot-announcement-live]')

  const scoreText = `${formatTrustpilotScoreDisplay(stats.stars, locale)}${scoreSuffix}`
  const reviewsText = `${formatTrustpilotReviewCount(stats.reviewCount, locale)}${reviewsSuffix}`

  if (scoreEl instanceof HTMLElement) {
    scoreEl.textContent = scoreText
  }

  if (reviewsEl instanceof HTMLElement) {
    reviewsEl.textContent = reviewsText
  }

  if (liveEl instanceof HTMLElement) {
    const lead =
      container
        .querySelector('.announcement-bar__trustpilot-copy-lead')
        ?.textContent?.trim() || ''
    const mid =
      container
        .querySelector('.announcement-bar__trustpilot-copy-mid')
        ?.textContent?.trim() || ''
    liveEl.textContent = `${lead}${scoreText}${mid}${reviewsText}`
      .replace(/\s+/g, ' ')
      .trim()
  }

  container.dataset.trustpilotAnnouncementHydrated = 'true'
  container.dataset.trustpilotAnnouncementFallbackVisible = 'false'
}

/**
 * @param {ParentNode} root
 */
export function hydrateAnnouncementTrustpilotRatings(root = document) {
  const scope =
    root instanceof Element || root instanceof Document ? root : document
  const containers = scope.querySelectorAll(ANNOUNCEMENT_SELECTOR)

  containers.forEach((container) => {
    if (!(container instanceof HTMLElement)) return
    if (container.dataset.trustpilotAnnouncementHydrated === 'true') {
      return
    }

    applyAnnouncementFallbackFromDataset(container)
    observeAnnouncementTrustpilotWidget(container)
  })
}

/**
 * @param {Element} widget
 * @returns {boolean}
 */
function loadTrustpilotWidgetElement(widget) {
  if (!(widget instanceof HTMLElement)) return false
  if (widget.getAttribute(LOADED_ATTR) === 'true') return true

  const trustpilot = /** @type {typeof window.Trustpilot | undefined} */ (
    window.Trustpilot
  )

  if (typeof trustpilot?.loadFromElement !== 'function') return false

  trustpilot.loadFromElement(widget, true)
  widget.setAttribute(LOADED_ATTR, 'true')

  const announcementContainer = widget.closest(ANNOUNCEMENT_SELECTOR)
  if (announcementContainer instanceof HTMLElement) {
    observeAnnouncementTrustpilotWidget(announcementContainer)
  }

  return true
}

/** @type {IntersectionObserver | null} */
let lazyWidgetObserver = null

/**
 * Below-fold badges (footer, cart drawer) wait until they approach the
 * viewport; the announcement badge hydrates the first slide and loads eagerly.
 *
 * @param {Element} widget
 * @returns {boolean}
 */
export function isTrustpilotWidgetDeferred(widget) {
  if (typeof window.IntersectionObserver !== 'function') return false
  if (widget.closest(ANNOUNCEMENT_SELECTOR)) return false
  return widget.getAttribute(NEAR_VIEWPORT_ATTR) !== 'true'
}

/**
 * @param {Element} widget
 */
function observeDeferredTrustpilotWidget(widget) {
  if (!lazyWidgetObserver) {
    lazyWidgetObserver = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue
          lazyWidgetObserver?.unobserve(entry.target)
          entry.target.setAttribute(NEAR_VIEWPORT_ATTR, 'true')
          loadTrustpilotWidgetElement(entry.target)
        }
      },
      { rootMargin: TRUSTPILOT_LAZY_ROOT_MARGIN }
    )
  }
  lazyWidgetObserver.observe(widget)
}

/**
 * @param {ParentNode} root
 * @param {number} [attempt]
 */
export function initTrustpilotWidgets(root = document, attempt = 0) {
  const scope =
    root instanceof Element || root instanceof Document ? root : document
  const widgets = scope.querySelectorAll(
    `${WIDGET_SELECTOR}:not([${LOADED_ATTR}="true"])`
  )

  hydrateAnnouncementTrustpilotRatings(scope)

  if (!widgets.length) return

  let pending = 0

  widgets.forEach((widget) => {
    if (isTrustpilotWidgetDeferred(widget)) {
      observeDeferredTrustpilotWidget(widget)
      return
    }
    if (loadTrustpilotWidgetElement(widget)) return
    pending += 1
  })

  if (pending > 0 && attempt < MAX_BOOTSTRAP_RETRIES) {
    window.setTimeout(() => {
      initTrustpilotWidgets(scope, attempt + 1)
    }, RETRY_DELAY_MS)
  }
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', () =>
    initTrustpilotWidgets(document)
  )
} else {
  initTrustpilotWidgets(document)
}

// The bootstrap is injected after load, often past the retry window above.
document.addEventListener(TRUSTPILOT_BOOTSTRAP_READY_EVENT, () =>
  initTrustpilotWidgets(document)
)

document.addEventListener('shopify:section:load', (event) => {
  const sectionId = event.detail?.sectionId || event.detail?.section_id
  const sectionRoot = sectionId
    ? document.getElementById(`shopify-section-${sectionId}`)
    : null

  initTrustpilotWidgets(sectionRoot || document)
})
